import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Check, Clock, X, Loader2, MapPin, CheckCircle2, FileText } from "lucide-react";

const S = {
  en: {
    job: "Job", accept: "Accept this job", accepted: "You've got this one",
    enroute: "I'm on the way", done: "Mark it done", photo: "Add a photo",
    more: "Need more time", cant: "I can't take it", taken: "Someone else already took this one.",
    needPhoto: "Send a photo to finish closing it out.", closed: "All closed out. Thank you.",
    before: "What was reported", after: "Your photos", eta: "How long?", mins: "min",
    sending: "Sending…", declined: "Thanks — the office and the manager have been told.",
    reported: "Reported", noPhoto: "No photo yet",
  },
  es: {
    job: "Trabajo", accept: "Aceptar este trabajo", accepted: "Este trabajo es tuyo",
    enroute: "Voy en camino", done: "Marcar como listo", photo: "Agregar una foto",
    more: "Necesito más tiempo", cant: "No puedo tomarlo", taken: "Alguien más ya lo tomó.",
    needPhoto: "Manda una foto para cerrarlo por completo.", closed: "Todo cerrado. Gracias.",
    before: "Lo que se reportó", after: "Tus fotos", eta: "¿Cuánto tiempo?", mins: "min",
    sending: "Enviando…", declined: "Gracias — avisamos a la oficina y al administrador.",
    reported: "Reportado", noPhoto: "Sin foto todavía",
  },
};

const Big = ({ onClick, children, testid, tone = "primary", disabled }) => {
  const tones = {
    primary: "bg-redline-500 text-white active:bg-redline-600",
    dark: "bg-ink text-white active:bg-ink/90",
    good: "bg-emerald-600 text-white active:bg-emerald-700",
    ghost: "bg-white text-ink border border-sand-300 active:bg-sand-100",
  };
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`flex w-full items-center justify-center gap-2.5 rounded-2xl px-5 py-4 text-base font-bold transition-colors disabled:opacity-50 ${tones[tone]}`}>
      {children}
    </button>
  );
};

export default function JobPage() {
  const { token } = useParams();
  const [j, setJ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [etaOpen, setEtaOpen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/fs/job/${token}`); setJ(data); }
    catch { setJ({ error: true }); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!j) return <div className="grid min-h-screen place-items-center bg-sand-50"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div>;
  if (j.error) return (
    <div className="grid min-h-screen place-items-center bg-sand-50 px-6 text-center">
      <p className="text-base text-ink/60">That link isn't valid any more.</p>
    </div>
  );

  const t = S[j.language === "es" ? "es" : "en"];
  const open = ["reported", "dispatched", "no_response", "overdue"].includes(j.status);
  const mine = ["scheduled", "enroute", "overdue"].includes(j.status);
  const finished = ["completed", "completed_unverified", "closed"].includes(j.status);

  const op = async (o, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fs/job/${token}/action/${o}`, body || {});
      if (data.already_taken) toast.error(t.taken);
      else if (o === "decline") toast.success(t.declined);
      else if (data.needs_photo) toast.info(t.needPhoto);
      await load();
    } catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false); setEtaOpen(false);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f); fd.append("kind", "after");
    try { await api.post(`/fs/job/${token}/photo`, fd); await load(); toast.success("✓"); }
    catch { toast.error("Photo didn't upload — try again"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-sand-50 pb-10" data-testid="job-page">
      <div className="mx-auto max-w-md px-5 pt-8">
        <div className="flex items-center justify-between">
          <span className="font-mono text-xs font-bold uppercase tracking-widest text-redline-600">{t.job} {j.ref}</span>
          <span className="text-xs font-semibold text-ink/40">{j.community}</span>
        </div>

        <h1 className="mt-3 font-heading text-2xl font-extrabold leading-tight text-ink">{j.summary}</h1>
        {j.location && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-ink/70">
            <MapPin className="h-3.5 w-3.5 text-redline-500" />{j.location}
          </div>
        )}

        {(j.scope || (j.scope_files || []).length > 0) && (
          <details className="mt-4 rounded-2xl border border-sand-200 bg-white px-4 py-3" data-testid="job-scope" open>
            <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-ink/45">
              {j.language === "es" ? "Alcance del trabajo" : "Scope of work"}{j.scope_revision ? ` · v${j.scope_revision}` : ""}
              {(j.scope_files || []).length ? ` · ${j.scope_files.length} ${j.language === "es" ? "documento(s)" : "document(s)"}` : ""}
            </summary>
            {j.scope && <pre data-testid="job-scope-text" className="mt-2 whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink/75">{j.scope}</pre>}
            {(j.scope_files || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {j.scope_files.map((f) => (
                  <a key={f.id} href={f.url} target="_blank" rel="noreferrer" data-testid={`job-scope-file-${f.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-sand-300 px-2.5 py-1 text-xs font-semibold text-ink/70">
                    <FileText className="h-3.5 w-3.5" /> {f.name}
                  </a>
                ))}
              </div>
            )}
          </details>
        )}

        {(j.before_urls || []).length > 0 && (
          <div className="mt-5">
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">{t.before}</div>
            <div className="flex gap-2 overflow-x-auto">
              {j.before_urls.map((u, i) => <img key={i} src={u} alt="reported" className="h-44 w-44 shrink-0 rounded-xl object-cover" />)}
            </div>
          </div>
        )}

        {(j.after_urls || []).length > 0 && (
          <div className="mt-4">
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">{t.after}</div>
            <div className="flex gap-2 overflow-x-auto">
              {j.after_urls.map((u, i) => <img key={i} src={u} alt="done" className="h-28 w-28 shrink-0 rounded-xl object-cover" />)}
            </div>
          </div>
        )}

        <div className="mt-7 space-y-2.5">
          {finished ? (
            <div className="rounded-2xl bg-emerald-50 px-5 py-6 text-center" data-testid="job-finished">
              <CheckCircle2 className="mx-auto h-7 w-7 text-emerald-600" />
              <p className="mt-2 text-base font-bold text-emerald-800">{t.closed}</p>
              {j.status === "completed_unverified" && (
                <>
                  <p className="mt-1 text-sm text-emerald-700/80">{t.needPhoto}</p>
                  <Big testid="job-photo-late" tone="ghost" onClick={() => fileRef.current?.click()}>
                    <Camera className="h-5 w-5" /> {t.photo}
                  </Big>
                </>
              )}
            </div>
          ) : open ? (
            <>
              {!etaOpen ? (
                <Big testid="job-accept" onClick={() => setEtaOpen(true)} disabled={busy}>
                  <Check className="h-5 w-5" /> {t.accept}
                </Big>
              ) : (
                <div className="rounded-2xl border border-sand-300 bg-white p-4">
                  <div className="mb-2.5 text-sm font-bold text-ink">{t.eta}</div>
                  <div className="grid grid-cols-4 gap-2">
                    {[10, 30, 60, 120].map((m) => (
                      <button key={m} data-testid={`job-eta-${m}`} onClick={() => op("accept", { eta_minutes: m })}
                        className="rounded-xl bg-ink py-3 text-sm font-bold text-white active:bg-ink/80">
                        {m} {t.mins}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Big testid="job-decline" tone="ghost" onClick={() => op("decline")} disabled={busy}>
                <X className="h-5 w-5" /> {t.cant}
              </Big>
            </>
          ) : (
            <>
              {mine && j.status !== "enroute" && (
                <Big testid="job-enroute" tone="dark" onClick={() => op("enroute")} disabled={busy}>
                  {t.enroute}
                </Big>
              )}
              <Big testid="job-photo" tone="ghost" onClick={() => fileRef.current?.click()} disabled={busy}>
                <Camera className="h-5 w-5" /> {t.photo}
              </Big>
              <Big testid="job-done" tone="good" onClick={() => op("complete")} disabled={busy}>
                <Check className="h-5 w-5" /> {t.done}
              </Big>
              <Big testid="job-more-time" tone="ghost" onClick={() => op("more-time")} disabled={busy}>
                <Clock className="h-5 w-5" /> {t.more}
              </Big>
            </>
          )}
        </div>

        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={upload} className="hidden" data-testid="job-file-input" />

        {(j.timeline || []).length > 0 && (
          <div className="mt-8 border-t border-sand-200 pt-4">
            {j.timeline.slice(-6).reverse().map((e, i) => (
              <div key={i} className="flex gap-2 py-1 text-xs text-ink/45">
                <span className="font-mono">{(e.at || "").slice(11, 16)}</span>
                <span className="font-semibold text-ink/60">{e.event}</span>
                <span className="truncate">{e.detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
